/* eslint-disable no-unused-vars */
import _ from 'lodash';
import moment from 'moment';
import { store } from 'redux/store';

import SitesService from './sites.service';
import TasksService from './tasks.service';
import SettingsService from './settings.service';
import PickListService from './picklist.service';
import InspectionService from './inspection.service';
import PipelineService from './pipeline.service';
import StructureService from './structure.service';
import http from './http.service';
import SyncingService from './inspection-work-order-checks-syncing.service';
import EquipmentService from './equipment.service';
import BookmarksService from './bookmarks.service';

import { homeActions } from '@redux/slices/home';
import { cacheActions } from '@redux/slices/cache';
import { getAppDispatch } from '@utilities/dispatch.util';
import { settingsActions } from '@redux/slices/settings';
import { filterActions } from '@redux/slices/filters';
import { globalProgressBarAction } from '@redux/slices/global-progress-bar';
import MobileSyncCountService from './mobile-sync-count.service';
import waitForLocalStorage from '@utilities/wait-for-local-storage';
import checkTokenIsGoingToExpire from '@utilities/token-expiry-check.util';
import AuthService from './auth.service';
import HSEEventService from './hse-event.service';
import ChecksService from './checks.service';

import { syncRecordsAndTasks } from './cache-services/sync-tasks-records';
import { fetchDataFromAllAPIs } from './cache-services/fetch-all-apis';
import { apisDataManipulationForStoringDataInIndexDB } from './cache-services/apis-data-manipulation';
import { storeDataInRedux } from './cache-services/store-data-on-redux';
import { allAPIsList } from './cache-services/all-apis-list';
import { isDBExists } from 'selectors/migration';
import { addMobileSyncCount } from 'transformers/mobile-sync-count';

const indexedDB =
  window.indexedDB ||
  window.mozIndexedDB ||
  window.webkitIndexedDB ||
  window.msIndexedDB ||
  window.shimIndexedDB;

const CacheService = {
  refresh: async ({
    initialCache = false,
    updatedSettings,
    sync,
    syncFailedAssets = false,
    workOrdersPendingSync = [],
    inspectionPendingSync = [],
    pendingHseEvents = [],
    pendingChecks = [],
    pendingEquipment = [],
    pendingSyncLength,
    bookmarks = [],
    updateBookmarks = [],
    deletedBookmarks = [],
    initialLogin = false,
    retrySync = false,
    updateApp = false,
    sendRequestToServerStartSyncing = false,
    pendingOrderPhotosDocumentsLength
  } = {}) => {
    localStorage.setItem('syncingAndRefreshingStart', 'yes');
    const { cache, auth, settings } = store.getState();
    const { lastSuccessFullRefreshTime } = cache;
    const { isSettingUpdated } = settings;

    const tokenIsExpire = checkTokenIsGoingToExpire();
    console.log({ tokenIsExpire });

    if (!tokenIsExpire.isValid) {
      await AuthService.logout({ clearCache: false });
      return [false, true];
    }

    if (tokenIsExpire?.shouldRefresh) {
      await AuthService.loginUsingRefreshToken({
        emailAddress: auth?.emailAddress || ''
      });
    }

    if (sync && sendRequestToServerStartSyncing) {
      await MobileSyncCountService.sendRequestToServerToStartSync({
        data: {
          ...addMobileSyncCount({ pendingOrderPhotosDocumentsLength })
        }
      });
    }

    const dispatch = getAppDispatch?.();

    dispatch?.(cacheActions.updateSyncingReminder(false));
    dispatch?.(settingsActions.setTokenExpireModalTime(''));

    // dispatch?.(filterActions.setTaskFilters(null));
    // dispatch?.(filterActions.setSitesFilters(null));
    // dispatch?.(filterActions.setEquipmentsFilters(null));

    dispatch?.(cacheActions.updateRefreshingAndSyncingData(true));

    if (sync) {
      await syncRecordsAndTasks({
        inspectionPendingSync,
        workOrdersPendingSync,
        pendingHseEvents,
        retrySync,
        syncFailedAssets,
        pendingChecks,
        pendingEquipment,
        pendingSyncLength
      });

      await MobileSyncCountService.requestServerToSyncIsCompleted();
    } else {
      // dispatch?.(cacheActions.setWorkOrderInspectionsAndChecksIsSync(false));
      dispatch?.(cacheActions.startCaching());
    }

    if (process.env.NODE_ENV !== 'development') {
      await waitForLocalStorage('isCacheComplete');
      // delay()
      dispatch?.(globalProgressBarAction.setLoadStatus(1)); //1
    } else dispatch?.(globalProgressBarAction.setLoadStatus(1));

    const [settingsUpdated] = await SettingsService.updateSettings(updatedSettings);
    if (settingsUpdated) {
      if (!_.isEmpty(bookmarks)) {
        await Promise.allSettled(
          bookmarks?.map(async (bookmark) => await BookmarksService.createBookmark(bookmark))
        );
      }
      if (!_.isEmpty(updateBookmarks)) {
        await Promise.allSettled(
          updateBookmarks?.map(
            async (bookmark) => await BookmarksService.updateBookmarksDataOnSync(bookmark)
          )
        );
      }
      if (!_.isEmpty(deletedBookmarks)) {
        await Promise.allSettled(
          deletedBookmarks?.map(
            async (bookmark) => await BookmarksService.deleteBookmarksOnSync(bookmark)
          )
        );
      }

      try {
        if (localStorage.getItem('cachingFailure') === 'true') {
          throw new Error('API Failed');
        }
        const apisList = allAPIsList({
          initialLogin,
          lastSuccessFullRefreshTime: isSettingUpdated ? '' : lastSuccessFullRefreshTime,
          settingsUpdated
        });

        const apisListMap = apisList?.reduce((acc, curr, currIndex) => {
          acc[curr.key] = currIndex;
          return acc;
        }, {});

        const apiListWithHttp = apisList?.map((x) => {
          return x?.method === 'POST' ? http.post(x.URL, x.body) : http.get(x?.URL);
        });

        dispatch?.(globalProgressBarAction.setLoadStatus(2));

        const allAPIsResponse = await fetchDataFromAllAPIs({
          apiListWithHttp,
          apisList,
          apisListMap
        });

        // await delay();

        dispatch?.(globalProgressBarAction.setLoadStatus(3));
        dispatch(cacheActions?.setDisabledCacheButtonOnRefresh(true));

        const manipulationJson = await apisDataManipulationForStoringDataInIndexDB({
          allApisResponse: allAPIsResponse,
          initialLogin,
          isSettingUpdated,
          apisList,
          apisListMap
        });

        dispatch?.(globalProgressBarAction.setLoadStatus(4));

        await storeDataInRedux({
          manipulationJson,
          apisList,
          apisListMap,
          initialLogin,
          isSettingUpdated
        });

        // check the old DB exists
        const oldDbExists = await isDBExists('my-oplii-test');

        if (updateApp && oldDbExists) localStorage.setItem('migrationIsDone', 'true');
        else if (!oldDbExists) localStorage.setItem('usedLatestDB', 'true');

        // if (settingsUpdated) await SettingsService.getSettings();

        if (!settingsUpdated) {
          if (sync) {
            //syncing error will be dealt here
            return [false, true];
          } else {
            //refreshing error dealt here
            !JSON.parse(localStorage.getItem('cachingFailure')) &&
              dispatch?.(cacheActions.failureCaching());
            return [false, true];
          }
        }
        dispatch?.(settingsActions.setSyncReminderFrequency(0));
        dispatch(cacheActions?.setDisabledCacheButtonOnRefresh(false));
        return [true, false];
      } catch (error) {
        dispatch(cacheActions?.setDisabledCacheButtonOnRefresh(false));
        console.log({ error });
        window.stop();
        dispatch?.(cacheActions.updateRefreshingAndSyncingData(false));
        dispatch?.(cacheActions.failureCaching());
        if (sync) {
          //syncing error will be dealt here
          return [false, true];
        } else {
          !JSON.parse(localStorage.getItem('cachingFailure')) &&
            dispatch?.(cacheActions.failureCaching());
          return [false, true];
        }
      } finally {
        const cachingFailure = localStorage.getItem('cachingFailure');

        if (!JSON.parse(cachingFailure)) {
          !sync && dispatch?.(cacheActions.completeCaching());
          // !sync &&
          dispatch?.(
            cacheActions.setLastSuccessFullRefreshTime(
              moment(new Date().toLocaleString('en-US', { timeZone: 'America/Denver' }))
                .format('YYYY-MM-DD HH:mm:ss')
                .toString()
            )
          );
        } else {
          dispatch?.(globalProgressBarAction.setLoadStatus(0));
        }
        if (initialCache) dispatch?.(homeActions.setInitialCache(true));
        if (initialLogin) await AuthService.successfulLoginUser();
        localStorage.removeItem('syncingAndRefreshingStart');
      }
    }
  },

  updateCacheFromIndexedDB: () => {
    if (!indexedDB) return;

    const request = indexedDB.open('OpliiCache', 1);

    request.onerror = (e) => console.error(e);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains('cache')) db.createObjectStore('cache', { keyPath: 'id' });
    };
    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction('cache', 'readwrite');
      const store = transaction.objectStore('cache');
      const cacheQuery = store.get(1);

      cacheQuery.onsuccess = () => {
        if (!cacheQuery.result?.value) return;

        const { value } = cacheQuery.result;
      };

      transaction.oncomplete = () => db.close();
    };
  },
  setPendingSyncTasksNumber: (tasksLength) => {
    const dispatch = getAppDispatch();
    dispatch?.(cacheActions.setPendingSync(tasksLength));
  },

  saveCacheInIndexedDB: (data) => {
    if (!indexedDB) return;

    const request = indexedDB.open('OpliiCache', 1);

    request.onerror = (e) => console.error(e);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains('cache')) db.createObjectStore('cache', { keyPath: 'id' });
    };
    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction('cache', 'readwrite');
      const store = transaction.objectStore('cache');
      const { sites = [], equipments = [], pendingSync } = data;
      const value = { pendingSync };

      store.put({ id: 1, value });

      transaction.oncomplete = () => db.close();
    };
  }
};

export default CacheService;

const delay = () => {
  return new Promise((resolve) => setTimeout(resolve, 60000));
};
